// BkosMoreClientsPage — /bkos/more-clients/:token. Lane mct_0cf5a1eb-855.
//
// A BKOS firm with no Extra Clients subscription asked for more clients; the
// Engine emailed this link. The page shows what they are buying (from the
// Engine, never page copy) with the Spiffy order form for that one pack
// embedded. Payment first: the purchase reaches the Engine, which then tells
// BKOS. The request token rides on the checkout so the purchase finds its
// request; the email is pre-filled and is how the Engine matches if not.
// Never a bare Spiffy link.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { SpiffyEmbedMount, spiffyAccountFromUrl } from './CheckoutPage';
import { spiffyUrlString } from './lib/spiffyUrl';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface FormData {
  state: string;
  plan_name?: string;
  clients_from?: number;
  clients_to?: number;
  extra_clients?: number;
  amount_cents?: number;
  currency?: string;
  gst_included?: boolean;
  plus_tax?: boolean;
  cadence?: string;
  checkout_url?: string;
  email?: string;
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.1, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 20px' };
const notice: CSSProperties = { fontSize: 15, color: '#0A0A2E', lineHeight: 1.5, background: '#FFF4E5', border: '1px solid #F5C88B', borderRadius: 10, padding: '12px 14px', margin: '0 0 16px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '20px 20px 22px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };

/** /bkos/more-clients/thanks: where the order form's Spiffy checkout sends the
 *  buyer after paying, so they never end on a Spiffy address (Michael, 25 Oct,
 *  mca_d1c9a9f49c05). In the 26 Oct live test BKOS raised the limit within
 *  seconds of the payment. */
export function BkosMoreClientsThanksPage() {
  return (
    <TakerShell footerLabel="BKOS by The Successful Bookkeeper">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 16px 80px' }}>
        <h1 style={h1}>Thank you. Your extra clients are on their way.</h1>
        <p style={lead}>
          Your payment went through, and we're adding the clients to your BKOS workspace now. It usually takes a minute
          or two. You'll see your new limit in BKOS under Settings, then Features.
        </p>
        <p style={lead}>
          Your receipt is on its way to your inbox. If your limit hasn't changed within an hour, reply to that email and
          we'll sort it out.
        </p>
      </div>
    </TakerShell>
  );
}

export default function BkosMoreClientsPage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<FormData | null>(null);
  const [failed, setFailed] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetch(`${API}/api/public/bkos-more-clients/${encodeURIComponent(token)}`)
      .then(async (r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<FormData> })
      .then(setData)
      .catch(() => setFailed(true));
  }, [token]);

  const checkoutUrl = useMemo(() => {
    if (!data?.checkout_url) return '';
    try {
      const u = new URL(data.checkout_url);
      if (data.email) u.searchParams.set('email', data.email);
      u.searchParams.set('bkos_request', token);
      return spiffyUrlString(u);
    } catch {
      return '';
    }
  }, [data, token]);
  const traits = useMemo(() => (data?.email ? { email: data.email } : null), [data?.email]);

  const per = data?.cadence === 'annual' ? 'year' : 'month';
  const price = data?.amount_cents != null
    ? `$${(data.amount_cents / 100).toFixed(2)} ${data.currency ?? ''}`.trim() + (data.gst_included ? ' incl. GST' : data.plus_tax ? ' plus tax' : '')
    : '';

  return (
    <TakerShell footerLabel="BKOS by The Successful Bookkeeper">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 16px 80px' }}>
        {failed && (
          <>
            <h1 style={h1}>This link isn't working</h1>
            <p style={lead}>It may have been replaced by a newer one. Reply to the email we sent and we'll sort it out.</p>
          </>
        )}
        {!failed && !data && <p style={lead}>Loading…</p>}
        {data && (data.state === 'paid' || data.state === 'applied') && (
          <>
            <h1 style={h1}>You're all set</h1>
            <p style={lead}>We have your payment{data.clients_to ? `, and your workspace takes up to ${data.clients_to} clients` : ''}. Nothing more to do here.</p>
          </>
        )}
        {data && data.state !== 'open' && data.state !== 'paid' && data.state !== 'applied' && (
          <>
            <h1 style={h1}>This order form is closed</h1>
            <p style={lead}>Reply to the email we sent if you still want more clients, and we'll send a fresh link.</p>
          </>
        )}
        {data && data.state === 'open' && (
          <>
            <h1 style={h1}>Add {data.extra_clients} clients to your BKOS workspace</h1>
            <p style={lead}>
              Your {data.plan_name} plan goes up to {data.clients_to} clients for {price} a {per}, on top of your plan.
              Pay below and we add them to your workspace straight away.
            </p>
            <p style={notice}>
              Please keep the email address below as it is. It's the one on your BKOS plan, so your receipt and your
              new clients land in the right place.
            </p>
            <div style={card}>
              {checkoutUrl
                ? (
                  <>
                    <div ref={wrapRef} className="spiffy-checkout-wrap" />
                    <SpiffyEmbedMount wrapRef={wrapRef} url={checkoutUrl} account={spiffyAccountFromUrl(checkoutUrl)} identifyTraits={traits} />
                  </>
                )
                : <p style={lead}>The order form isn't ready yet. Reply to the email we sent and we'll help.</p>}
            </div>
          </>
        )}
      </div>
    </TakerShell>
  );
}
